import { useState, useEffect, type MouseEvent, type SubmitEvent, type ReactNode } from 'react';
import { type NavigateFunction } from 'react-router-dom';
import { authHeaders } from '../../utils/auth';
import { apiFetch } from '../../utils/api';
import { getStatusBadge } from '../../utils/batchStatus';
import type { Batch, User, BatchOrigin } from '../../types';
import ApprovalTypeBadge from '../shared/ApprovalTypeBadge';
import OriginBadge from '../shared/OriginBadge';
import BatchOriginsModal from './BatchOriginsModal';

interface Props {
    batches: Batch[];
    navigate: NavigateFunction;
    refreshBatches: () => Promise<void>;
    onSelectBatch: (batch: Batch) => void;
    variant?: 'admin' | 'ad';
    extraHeaderAction?: ReactNode;
    hideUploadButton?: boolean;
    reviewSelectionMode?: boolean;
    reviewBatchIds?: Set<number>;
    onToggleReviewBatch?: (id: number) => void;
}

const emptyForm = { year: '', photographer: '', galleryName: '', description: '', assignedAdId: '', approvalType: 'agma', batchOriginId: '' };

export default function BatchListView({ batches, navigate, refreshBatches, onSelectBatch, variant = 'admin', extraHeaderAction, hideUploadButton, reviewSelectionMode, reviewBatchIds, onToggleReviewBatch }: Props) {
    const isAdmin = variant === 'admin';

    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [formError, setFormError] = useState('');

    const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
    const [editFormData, setEditFormData] = useState(emptyForm);
    const [editError, setEditError] = useState('');
    const [editSaving, setEditSaving] = useState(false);

    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Batch | null>(null);
    const [deleteInput, setDeleteInput] = useState('');

    const [asMembers, setAsMembers] = useState<User[]>([]);
    const [origins, setOrigins] = useState<BatchOrigin[]>([]);
    const [showOriginsModal, setShowOriginsModal] = useState(false);


    useEffect(() => {
        if (!isAdmin) return;
        Promise.all([
            apiFetch('/api/users?role=artistic_director', { headers: authHeaders() }).then(r => r.json()),
            apiFetch('/api/batch-origins', { headers: authHeaders() }).then(r => r.json()),
        ])
            .then(([adData, originData]: [unknown, unknown]) => {
                setAsMembers(Array.isArray(adData) ? adData : []);
                setOrigins(Array.isArray(originData) ? originData : []);
            })
            .catch(() => {});
    }, [isAdmin]);

    const initiateDelete = (batch: Batch) => {
        setDeleteTarget(batch);
        setDeleteInput('');
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const id = deleteTarget.id;
        setDeletingId(id);
        setDeleteTarget(null);
        setDeleteInput('');
        try {
            const res = await apiFetch(`/api/batches/${id}`, { method: 'DELETE', headers: authHeaders() });
            if (res.ok) refreshBatches();
            else alert('Failed to delete batch.');
        } catch {
            alert('Failed to delete batch.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleCreate = async (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormError('');
        const body = {
            year: formData.year,
            photographer: formData.photographer,
            galleryName: formData.galleryName,
            description: formData.description,
            assignedAdId: formData.assignedAdId ? parseInt(formData.assignedAdId, 10) : null,
            approvalType: formData.approvalType || 'agma',
            batchOriginId: formData.batchOriginId ? parseInt(formData.batchOriginId, 10) : null,
        };
        const res = await apiFetch('/api/batches', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body),
        });
        if (res.ok) {
            const data: { id: number } = await res.json();
            navigate(`/admin/upload/${data.id}`);
        } else {
            const err = await res.json().catch(() => ({}));
            setFormError(err.message || 'Failed to create batch');
        }
    };

    const handleEditClick = (batch: Batch, e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setEditingBatch(batch);
        setEditFormData({
            year: batch.year,
            photographer: batch.photographer,
            galleryName: batch.galleryName,
            description: batch.description ?? '',
            assignedAdId: batch.assignedAdId != null ? String(batch.assignedAdId) : '',
            approvalType: batch.approvalType ?? 'agma',
            batchOriginId: batch.batchOriginId != null ? String(batch.batchOriginId) : '',
        });
        setEditError('');
        setShowForm(false);
        setFormError('');
    };

    const handleUpdate = async (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!editingBatch) return;
        setEditSaving(true);
        setEditError('');
        const body: Record<string, unknown> = {
            year: editFormData.year,
            photographer: editFormData.photographer,
            galleryName: editFormData.galleryName,
            description: editFormData.description,
            assignedAdId: editFormData.assignedAdId ? parseInt(editFormData.assignedAdId, 10) : null,
            batchOriginId: editFormData.batchOriginId ? parseInt(editFormData.batchOriginId, 10) : null,
        };
        try {
            const res = await apiFetch(`/api/batches/${editingBatch.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(body),
            });
            if (res.ok) {
                setEditingBatch(null);
                setEditFormData(emptyForm);
                await refreshBatches();
            } else {
                const err = await res.json().catch(() => ({}));
                setEditError(err.message || 'Failed to update batch');
            }
        } catch {
            setEditError('Failed to update batch');
        } finally {
            setEditSaving(false);
        }
    };

    const asMemberDropdown = (value: string, onChange: (v: string) => void) => (
        <div className='user-form-field'>
            <label>Artistic Staff <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
            <select value={value} onChange={e => onChange(e.target.value)}>
                <option value=''>— Unassigned —</option>
                {asMembers.map(u => (
                    <option key={u.id} value={String(u.id)}>
                        {u.fName} {u.lName}
                    </option>
                ))}
            </select>
        </div>
    );

    const batchForm = (
        title: string,
        data: typeof emptyForm,
        setData: (fn: (p: typeof emptyForm) => typeof emptyForm) => void,
        onSubmit: (e: SubmitEvent<HTMLFormElement>) => void,
        onCancel: () => void,
        error: string,
        submitLabel: string,
        saving?: boolean,
        showApprovalType?: boolean,
    ) => (
        <form className='user-form' onSubmit={onSubmit}>
            <h3 className='user-form-title'>{title}</h3>
            {error && <p className='user-form-error'>{error}</p>}
            <div className='user-form-row'>
                <div className='user-form-field'>
                    <label>Year</label>
                    <input
                        placeholder='e.g. 2026'
                        value={data.year}
                        onChange={e => setData(p => ({ ...p, year: e.target.value }))}
                        maxLength={4}
                        required
                    />
                </div>
                <div className='user-form-field'>
                    <label>Photographer</label>
                    <input
                        placeholder='Photographer name'
                        value={data.photographer}
                        onChange={e => setData(p => ({ ...p, photographer: e.target.value }))}
                        required
                    />
                </div>
            </div>
            <div className='user-form-row'>
                <div className='user-form-field'>
                    <label>Gallery Name</label>
                    <input
                        placeholder='Gallery name'
                        value={data.galleryName}
                        onChange={e => setData(p => ({ ...p, galleryName: e.target.value }))}
                        required
                    />
                </div>
                <div className='user-form-field'>
                    <label>Description <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
                    <input
                        placeholder='Optional description'
                        value={data.description}
                        onChange={e => setData(p => ({ ...p, description: e.target.value }))}
                    />
                </div>
            </div>
            <div className='user-form-row'>
                {asMemberDropdown(data.assignedAdId, v => setData(p => ({ ...p, assignedAdId: v })))}
                {showApprovalType && (
                    <div className='user-form-field'>
                        <label>Approval Type</label>
                        <select value={data.approvalType} onChange={e => setData(p => ({ ...p, approvalType: e.target.value }))}>
                            <option value='agma'>AGMA Approval</option>
                            <option value='single_point'>Single Point Approval</option>
                        </select>
                    </div>
                )}
            </div>
            <div className='user-form-row'>
                <div className='user-form-field'>
                    <label>Batch Origin <span className='field-label-hint'>(source label for exported filenames)</span></label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <button type='button' className='origins-manage-btn' onClick={() => setShowOriginsModal(true)}>
                            Manage Origins
                        </button>
                    </div>
                    <select value={data.batchOriginId} onChange={e => setData(p => ({ ...p, batchOriginId: e.target.value }))}>
                        <option value=''>— None —</option>
                        {origins.filter(o => o.isActive).map(o => (
                            <option key={o.id} value={String(o.id)}>{o.name}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className='user-form-actions'>
                <button type='button' className='user-cancel-btn' onClick={onCancel}>Cancel</button>
                <button type='submit' className='user-submit-btn' disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
            </div>
        </form>
    );

    return (
        <div className='batch-list'>
            <div className='batch-list-header'>
                <h2 className='batch-list-title'>Batches</h2>
                {isAdmin && !showForm && !editingBatch && (extraHeaderAction || !hideUploadButton) && (
                    <div className='batch-list-header-actions'>
                        {extraHeaderAction}
                        {!hideUploadButton && (
                            <button className='user-submit-btn' onClick={() => setShowForm(true)}>
                                + Upload New Batch
                            </button>
                        )}
                    </div>
                )}
            </div>

            {isAdmin && showForm && batchForm(
                'New Batch',
                formData,
                setFormData,
                handleCreate,
                () => { setShowForm(false); setFormData(emptyForm); setFormError(''); },
                formError,
                'Create Batch',
                undefined,
                true,
            )}

            {isAdmin && editingBatch && batchForm(
                `Edit — ${editingBatch.galleryName}`,
                editFormData,
                setEditFormData,
                handleUpdate,
                () => { setEditingBatch(null); setEditFormData(emptyForm); setEditError(''); },
                editError,
                'Save Changes',
                editSaving,
            )}

            {batches.length === 0 ? (
                <p className='batch-list-empty'>No batches yet.</p>
            ) : (
                <div className='table-responsive'>
                    <table className='batch-table'>
                        <thead>
                            <tr>
                                <th>Production</th>
                                <th>Approval</th>
                                <th>Origin</th>
                                <th>Status</th>
                                {isAdmin && <th>Artistic Staff</th>}
                                {isAdmin && <th>Actions</th>}
                                {!isAdmin && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {batches.map(batch => {
                                const { label, cls } = getStatusBadge(batch, variant);
                                const adClickable = !isAdmin && (
                                    batch.status === 'uploaded' ||
                                    batch.status === 'ad_selected' ||
                                    batch.status === 'pending' ||
                                    batch.status === 'processed'
                                );
                                const isOriginalBatch = batch.batchType === 'original';
                                const rowClickable = reviewSelectionMode
                                    ? isOriginalBatch
                                    : (isAdmin || adClickable);
                                return (
                                    <tr
                                        key={batch.id}
                                        className={`batch-table-row${rowClickable ? '' : ' batch-table-row--inert'}`}
                                        onClick={
                                            reviewSelectionMode
                                                ? (isOriginalBatch ? () => onToggleReviewBatch?.(batch.id) : undefined)
                                                : (isAdmin || adClickable ? () => onSelectBatch(batch) : undefined)
                                        }
                                    >
                                        <td>
                                            {reviewSelectionMode && isOriginalBatch && (
                                                <input
                                                    type='checkbox'
                                                    checked={reviewBatchIds?.has(batch.id) ?? false}
                                                    onChange={() => onToggleReviewBatch?.(batch.id)}
                                                    onClick={e => e.stopPropagation()}
                                                    style={{ marginRight: 8, cursor: 'pointer', verticalAlign: 'middle' }}
                                                />
                                            )}
                                            {batch.galleryName}
                                            {batch.batchType === 'secondary' && (
                                                <span className='batch-secondary-label'>Secondary</span>
                                            )}
                                            {isAdmin && batch.assignedAd && (
                                                <span className='batch-col-as-mobile'>
                                                    AS: {batch.assignedAd.fName} {batch.assignedAd.lName}
                                                </span>
                                            )}
                                        </td>
                                        <td><ApprovalTypeBadge approvalType={batch.approvalType} /></td>
                                        <td><OriginBadge origin={batch.assignedOrigin} /></td>
                                        <td>
                                            <span className={`batch-status-badge ${cls}`}>{label}</span>
                                            {batch.emailAlert && (
                                                <span className='batch-email-alert'>⚠ Email issue</span>
                                            )}
                                            {batch.releasedToGroups && batch.releasedToGroups.length > 0 && (
                                                <span className='batch-released-badge'>
                                                    Released to {batch.releasedToGroups.join(', ')}
                                                </span>
                                            )}
                                        </td>
                                        {isAdmin && (
                                            <td onClick={e => e.stopPropagation()}>
                                                {batch.assignedAd
                                                    ? `${batch.assignedAd.fName} ${batch.assignedAd.lName}`
                                                    : <span style={{ color: '#aaa' }}>—</span>}
                                            </td>
                                        )}
                                        {isAdmin && (
                                            <td onClick={e => e.stopPropagation()}>
                                                {(batch.status === 'uploaded' || batch.status === 'empty') && batch.batchType !== 'secondary' && (
                                                    <button
                                                        className='batch-action-btn'
                                                        style={{ marginRight: 'var(--space-2)' }}
                                                        onClick={() => navigate(`/admin/upload/${batch.id}`)}
                                                    >
                                                        Add Photos
                                                    </button>
                                                )}
                                                <button
                                                    className='user-edit-btn'
                                                    onClick={e => handleEditClick(batch, e)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className='batch-action-btn danger'
                                                    onClick={() => initiateDelete(batch)}
                                                    disabled={deletingId === batch.id}
                                                >
                                                    {deletingId === batch.id ? 'Deleting…' : 'Delete'}
                                                </button>
                                            </td>
                                        )}
                                        {!isAdmin && (
                                            <td onClick={e => e.stopPropagation()}>
                                                {batch.status === 'uploaded' && (
                                                    <button className='batch-action-btn' onClick={() => onSelectBatch(batch)}>
                                                        Select Photos
                                                    </button>
                                                )}
                                                {batch.status === 'ad_selected' && (
                                                    <button className='batch-action-btn' onClick={() => onSelectBatch(batch)}>
                                                        Edit Selections
                                                    </button>
                                                )}
                                                {(batch.status === 'pending' || batch.status === 'processed') && (
                                                    <button className='batch-action-btn' onClick={() => onSelectBatch(batch)}>
                                                        View Status
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {isAdmin && deleteTarget && (
                <div className='delete-modal-backdrop'>
                    <div className='delete-modal'>
                        {deleteTarget.status === 'pending' && (
                            <p className='delete-modal-warning'>
                                ⚠️ This batch is currently in review. Artists may still be approving photos.
                            </p>
                        )}
                        <p className='delete-modal-body'>
                            This will permanently delete all photos and approval records for{' '}
                            <strong>{deleteTarget.galleryName} — {deleteTarget.year}</strong>.
                        </p>
                        <p className='delete-modal-prompt'>
                            Type <strong>{deleteTarget.galleryName}</strong> to confirm:
                        </p>
                        <input
                            className='delete-modal-input'
                            type='text'
                            value={deleteInput}
                            onChange={e => setDeleteInput(e.target.value)}
                            placeholder={deleteTarget.galleryName}
                            autoFocus
                            onKeyDown={e => {
                                if (e.key === 'Enter' && deleteInput === deleteTarget.galleryName) confirmDelete();
                                if (e.key === 'Escape') { setDeleteTarget(null); setDeleteInput(''); }
                            }}
                        />
                        <div className='delete-modal-actions'>
                            <button className='user-cancel-btn' onClick={() => { setDeleteTarget(null); setDeleteInput(''); }}>
                                Cancel
                            </button>
                            <button
                                className='batch-action-btn danger'
                                onClick={confirmDelete}
                                disabled={deleteInput !== deleteTarget.galleryName}
                            >
                                Delete Batch
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showOriginsModal && (
                <BatchOriginsModal
                    onClose={() => setShowOriginsModal(false)}
                    onOriginsChange={setOrigins}
                />
            )}


        </div>
    );
}